import {useCallback, useEffect, useState} from 'react';

import {referralService} from '@/services/referralService';
import type {AdminAfriexPerson} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

import type {TierListProps} from './types';

const useTierUserList = (
  tierId: string,
): TierListProps<Partial<AdminAfriexPerson>> => {
  const [tierUserList, setTierUserList] = useState<
    Partial<AdminAfriexPerson>[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [error, setError] = useState<unknown>(null);

  const fetchTierUserList = useCallback(
    async (params: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data, total} = await referralService.getTierUserList(
          tierId,
          params,
        );
        setTierUserList(data);
        setItemTotal(total || 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [tierId],
  );

  useEffect(() => {
    // Fire-and-forget: fetchTierUserList try/catches, stores the failure in
    // `error` and clears `isLoading`, so this only stops an unexpected throw
    // becoming an unhandled rejection.
    fetchTierUserList({}).catch(() => {});
  }, [fetchTierUserList]);

  return {
    error,
    fetchList: fetchTierUserList,
    isLoading,
    page,
    setPage,
    total: itemTotal,
    list: tierUserList,
  };
};

export default useTierUserList;
